import moment from 'moment';
import { Course, SelectedAssignment } from './types';

/**
 * A course is active until its end date - the same test as the ACTIVE/INACTIVE badge on the
 * Overview screen. Courses still assigned to this set stay visible whatever their end date,
 * so an assignment can always be removed from the screen that created it.
 */
/**
 * Whether the course is still running by its end date alone, ignoring assignment sets - the
 * same test as the ACTIVE/INACTIVE badge on the Overview screen. Split out so "has the end date
 * passed" has one definition; isCourseActive builds on it.
 */
export const isWithinEndDate = (course: Course): boolean => moment().isBefore(course.end_date);

export const isCourseActive = (course: Course, assignmentSetId: unknown): boolean => {
  // 0 is the value used to unassign a course, and the id starts out as a non-number before
  // an existing set is loaded, so both have to be excluded: matching on either would treat
  // every unassigned course as belonging to this set and show ended ones for ever.
  const assignedToThisSet = typeof assignmentSetId === 'number'
    && assignmentSetId > 0
    && course.assignment_set_id === assignmentSetId;

  return isWithinEndDate(course) || assignedToThisSet;
};

export interface BonusSelectionInput {
  courses: Course[];
  selectedCourseIds: number[];
  bonusCourseIds: number[];
  seededCourseIds: number[];
  isEdit: boolean;
}

export interface BonusSelection {
  bonusCourseIds: number[];
  seededCourseIds: number[];
  changed: boolean;
}

/**
 * Works out which courses should have Persistence with Bonus ticked on the bonus step.
 *
 * Editing an existing set starts each course from its current with_bonus value; a brand new
 * set defaults everything on. Re-running is a no-op, so returning to the assign step and
 * changing the selection adds or drops courses without discarding toggles already made.
 *
 * Returns the next selection plus the courses now accounted for, so the caller can tell an
 * unseeded course (needs a default) from one the user has deliberately unticked.
 */
export const nextBonusSelection = ({
  courses, selectedCourseIds, bonusCourseIds, seededCourseIds, isEdit,
}: BonusSelectionInput): BonusSelection => {
  const kept = bonusCourseIds.filter((id) => selectedCourseIds.includes(id));
  const unseeded = selectedCourseIds.filter((id) => !seededCourseIds.includes(id));
  const defaultedOn = unseeded.filter((id) => {
    if (!isEdit) return true;
    const course = courses.find((current) => current.id === id);
    return course ? Boolean(course.with_bonus) : true;
  });

  return {
    bonusCourseIds: [...kept, ...defaultedOn],
    seededCourseIds: [...selectedCourseIds],
    changed: unseeded.length > 0
      || seededCourseIds.some((id) => !selectedCourseIds.includes(id)),
  };
};

// AssignmentSummary expects lesson order; copy before sorting so we never mutate state in a render path.
export const sortByLesson = (assignments: SelectedAssignment[]): SelectedAssignment[] => [...assignments]
  .sort((a, b) => a.lesson.display_order - b.lesson.display_order);
